import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimCalculationData, BimWorkOrderData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { getWorkOrderStatusLabel } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { formatNumberToRu, newDate } from 'utils';

import { useCallback, useMemo } from 'react';

import { useSetIsWorkOrderChangeRequired } from '../../hooks/useSetIsWorkOrderChangeRequired';

import { bimUpdateCalculationRequisitesGql, updateBimOrderRequisitesGql } from './bimWorkOrderRequisitesQueries';

export const useTransformRequisitesData = ({
    defaultValues,
    isFrameContract,
    onSuccess,
}: {
    defaultValues: BimWorkOrderData | BimCalculationData | undefined | null;
    isFrameContract: boolean;
    onSuccess: () => void;
}) => {
    const preparedDefaultRequisitesData = useMemo<any>(() => {
        const commonValues = {
            ...defaultValues,
            status: {
                id: defaultValues?.status,
                label: getWorkOrderStatusLabel(defaultValues?.status ?? WorkOrderStatusEnum.OnFormation),
            },
            ...defaultValues?.seasonDistributions?.reduce(
                (acc, value) => ({
                    ...acc,
                    [`season_${value.workOrderSeasonId}`]: `${formatNumberToRu(value.seasonValue, 2) ?? 0} %`,
                }),
                {},
            ),
        };

        if (isFrameContract) {
            const frameContractData = defaultValues as BimWorkOrderData;

            const amountWithTax =
                frameContractData?.amountWithTax === null ? 0 : formatNumberToRu(frameContractData?.amountWithTax, 2);
            const amountWithoutTax =
                frameContractData?.amountWithoutTax === null
                    ? 0
                    : formatNumberToRu(frameContractData?.amountWithoutTax, 2);
            const tax = frameContractData?.tax === null ? 0 : formatNumberToRu(frameContractData?.tax, 2);
            const remainder =
                frameContractData?.remainder === null ? 0 : formatNumberToRu(frameContractData?.remainder, 2);
            const withWorkOrderChangeInfo = Boolean(frameContractData?.workOrderChange);

            return {
                ...commonValues,
                signingDate: newDate(frameContractData?.signingDate),
                amountWithTax,
                amountWithoutTax,
                tax,
                remainder,
                workOrderChange: withWorkOrderChangeInfo
                    ? {
                          ...frameContractData?.workOrderChange,
                          signingDate: newDate(frameContractData?.signingDate),
                      }
                    : null,
                rangeDate: [newDate(frameContractData?.startDate), newDate(frameContractData?.finishDate)],
            };
        }

        return commonValues;
    }, [defaultValues, isFrameContract]);

    const [resetUpdateData] = useResetStoreData({
        requestId: 'updateOrderRequisites',
    });

    const successUpdateHandler = useCallback(() => {
        onSuccess?.();
        resetUpdateData(undefined);
    }, [onSuccess, resetUpdateData]);

    const [updateOrderData, updateOrderStatus, , updateBimOrderRequisitesRequest] = useRequest({
        requestId: 'updateOrderRequisites',
        query: updateBimOrderRequisitesGql,
        withEffect: false,
        dependenciesUpdater: successUpdateHandler,
    });

    useSetIsWorkOrderChangeRequired({
        useData: updateOrderData?.bimUpdateWorkOrder?.data,
        status: updateOrderStatus,
    });

    const [updateOrderCalcData, updateOrderCalcStatus, , updateBimOrderCalcRequisitesRequest] = useRequest({
        requestId: 'updateOrderRequisites',
        query: bimUpdateCalculationRequisitesGql,
        withEffect: false,
        dependenciesUpdater: successUpdateHandler,
    });

    useSetIsWorkOrderChangeRequired({
        useData: updateOrderCalcData?.bimUpdateCalculation?.data,
        status: updateOrderCalcStatus,
    });

    // TODO any
    const getTransformedData: any = useCallback(
        (data: {
            id: number;
            status: { id: WorkOrderStatusEnum };
            rangeDate: [Date, Date];
            internalNumber: number;
            signingDate: Date;
            subject: string;
            [key: string]: any;
        }) => {
            if (isFrameContract) {
                return {
                    workOrderInput: {
                        id: data.id,
                        finishDate: data.rangeDate[1],
                        startDate: data.rangeDate[0],
                        internalNumber: data.internalNumber,
                        signingDate: data.signingDate,
                        status: data.status.id,
                        subject: data.subject,
                    },
                };
            }

            return {
                calculationInput: { id: data.id, status: data.status.id },
            };
        },
        [isFrameContract],
    );

    const updateBimOrderRequisites = useMemo(
        () => (isFrameContract ? updateBimOrderRequisitesRequest : updateBimOrderCalcRequisitesRequest),
        [isFrameContract, updateBimOrderCalcRequisitesRequest, updateBimOrderRequisitesRequest],
    );

    const isUpdating = useMemo(
        () => updateOrderStatus === RequestStatus.FETCHING || updateOrderCalcStatus === RequestStatus.FETCHING,
        [updateOrderCalcStatus, updateOrderStatus],
    );

    return {
        preparedDefaultRequisitesData,
        getTransformedData,
        updateBimOrderRequisites,
        isUpdating,
        updateOrderStatus,
    };
};
